import { useEffect, useRef, useState } from "react";
import { markLandingInteraction } from "./appHandoff";

// Listen on the document so the whole landing page accepts a file, including the footer.
// Text/link drags retain their normal browser behavior. A counter prevents child transitions
// from flashing the overlay; it never intercepts pointer events itself.
export function usePageFileDrop(onFiles: (files: File[]) => void, disabled: boolean): boolean {
  const [isDragging, setIsDragging] = useState(false);
  const callbackRef = useRef(onFiles);
  const disabledRef = useRef(disabled);
  callbackRef.current = onFiles;
  disabledRef.current = disabled;

  useEffect(() => {
    let depth = 0;
    const hasFiles = (event: DragEvent) => Array.from(event.dataTransfer?.types || []).includes("Files");
    const reset = () => { depth = 0; setIsDragging(false); };
    const enter = (event: DragEvent) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      markLandingInteraction();
      depth += 1;
      if (!disabledRef.current) setIsDragging(true);
    };
    const over = (event: DragEvent) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = disabledRef.current ? "none" : "copy";
    };
    const leave = (event: DragEvent) => {
      if (!hasFiles(event)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0 || (event.target === document.documentElement && !event.relatedTarget)) reset();
    };
    const drop = (event: DragEvent) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      reset();
      if (disabledRef.current) return;
      const items = Array.from(event.dataTransfer?.items || []);
      const hasDirectory = items.some((item) => item.webkitGetAsEntry?.()?.isDirectory);
      callbackRef.current(hasDirectory ? [] : Array.from(event.dataTransfer?.files || []));
    };
    const key = (event: KeyboardEvent) => { if (event.key === "Escape") reset(); };
    document.addEventListener("dragenter", enter);
    document.addEventListener("dragover", over);
    document.addEventListener("dragleave", leave);
    document.addEventListener("drop", drop);
    document.addEventListener("keydown", key);
    window.addEventListener("blur", reset);
    window.addEventListener("pagehide", reset);
    return () => {
      document.removeEventListener("dragenter", enter);
      document.removeEventListener("dragover", over);
      document.removeEventListener("dragleave", leave);
      document.removeEventListener("drop", drop);
      document.removeEventListener("keydown", key);
      window.removeEventListener("blur", reset);
      window.removeEventListener("pagehide", reset);
    };
  }, []);
  return isDragging && !disabled;
}
